'use client';

import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { cn } from '@pickleballinc/ui/lib/utils';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';

import { ScheduleMatchup } from '@/entities';
import { useOrganizationStore } from '@/store/organization-store';

interface MatchupCardProps {
	matchup: ScheduleMatchup;
	className?: string;
}

const formatMatchPlannedStartDate = (date: string | undefined, timezone: string) =>
	date ? dayjs.utc(date).tz(timezone).format('MMM D YYYY h:mm A') : '';

export const MatchupCard = ({ matchup, className = '' }: MatchupCardProps) => {
	const store = useOrganizationStore();
	const router = useRouter();

	const formattedMatchup = {
		matchStart: formatMatchPlannedStartDate(matchup.plannedStartDate, matchup.timezone?.ianaTzIdentifier || 'UTC') || '',
		courts: matchup.courts.map((court) => court.title).join(', ')
	};

	const away = {
		uuid: matchup.teamLeagueTeamTwoUuid,
		logo: matchup.teamTwoLogo?.url,
		title: matchup.teamTwoTitle ?? 'TBD',
		ranking: matchup?.teamTwoRanking,
		rating: matchup?.teamTwoRating?.toFixed(2),
		pointsDiffPct: matchup.teamTwoPointsDiffPct || 0,
		score: matchup.teamTwoScore ?? 0,
		link: `/team-leagues/${store.teamLeagueSlug}/team-details/${matchup.teamLeagueTeamTwoUuid}/stats`
	};

	const home = {
		uuid: matchup.teamLeagueTeamOneUuid,
		logo: matchup.teamOneLogo?.url,
		title: matchup.teamOneTitle ?? 'TBD',
		ranking: matchup?.teamOneRanking,
		rating: matchup?.teamOneRating,
		pointsDiffPct: matchup.teamOnePointsDiffPct || 0,
		score: matchup.teamOneScore ?? 0,
		link: `/team-leagues/${store.teamLeagueSlug}/team-details/${matchup.teamLeagueTeamOneUuid}/stats`
	};

	const handleRedirectToTeamDetailsPage = (team: { title: string; link: string }, event: React.MouseEvent<HTMLElement>) => {
		if (team.title === 'TBD') {
			return;
		}
		event.stopPropagation();
		event.preventDefault();
		router.push(team.link);
	};

	return (
		<CustomLink href={`/team-leagues/${store.teamLeagueSlug}/matchup/${matchup.uuid}`} className="cursor-pointer">
			<div className={cn('bg-white rounded-lg border border-gray-200 px-4 py-3', className)}>
				<div className="mb-1.5">
					<div className="text-sm text-gray-500">
						{formattedMatchup.matchStart} {matchup?.timezone?.abbreviation}
					</div>
					{!!matchup?.matchupGroupTitle && <div className="text-sm text-gray-500">{matchup.matchupGroupTitle}</div>}
				</div>

				<div className="mb-1.5">
					<div className="block md:hidden">
						<div className="flex items-center gap-3 mb-3">
							{!!away.logo && (
								<span className="inline-block align-middle w-32 h-16 rounded-sm bg-white">
									<img src={getCDNURL(away.logo, 240, 120)} alt={away.title} className="w-full h-full object-contain rounded-sm" />
								</span>
							)}
							<div>
								<div
									className={clsx('text-base font-semibold break-words whitespace-normal line-clamp-2', {
										'text-orange-500 hover:underline': away.title !== 'TBD'
									})}
									onClick={(e) => handleRedirectToTeamDetailsPage(away, e)}
									role="presentation"
								>
									{away.title}
								</div>

								<div className="text-sm text-gray-500">Away {!!away.ranking && <span className="ml-1">#{away.ranking}</span>}</div>
							</div>
						</div>

						<div className="flex items-center justify-center mb-3">
							<div className="flex-1 h-px bg-orange-100" />
							<span className="text-sm text-gray-500 font-medium px-4">vs</span>
							<div className="flex-1 h-px bg-orange-100" />
						</div>

						<div className="flex items-center gap-3">
							{!!home.logo && (
								<span className="inline-block align-middle w-32 h-16 rounded-sm bg-white">
									<img src={getCDNURL(home.logo, 240, 120)} alt={home.title} className="w-full h-full object-contain rounded-sm" />
								</span>
							)}
							<div>
								<div
									className={clsx('text-base font-semibold break-words whitespace-normal line-clamp-2', {
										'text-orange-500 hover:underline': home.title !== 'TBD'
									})}
									onClick={(e) => handleRedirectToTeamDetailsPage(home, e)}
									role="presentation"
								>
									{home.title}
								</div>

								<div className="text-sm text-gray-500">Home {!!home.ranking && <span className="ml-1">#{home.ranking}</span>}</div>
							</div>
						</div>
					</div>

					<div className="hidden md:flex items-center gap-6">
						<div className="flex items-center gap-3">
							{!!away.logo && (
								<span className="inline-block align-middle w-28 h-14 rounded-sm bg-white">
									<img src={getCDNURL(away.logo, 220, 110)} alt={away.title} className="w-full h-full object-contain rounded-sm" />
								</span>
							)}
							<div>
								<div
									className={clsx('text-base font-semibold break-words whitespace-normal line-clamp-2', {
										'text-orange-500 hover:underline': away.title !== 'TBD'
									})}
									onClick={(e) => handleRedirectToTeamDetailsPage(away, e)}
									role="presentation"
								>
									{away.title}
								</div>

								<div className="text-sm text-gray-500">Away {!!away.ranking && <span className="ml-1">#{away.ranking}</span>}</div>
							</div>
						</div>

						<span className="text-sm text-gray-500 font-medium">vs</span>

						<div className="flex items-center gap-3">
							{!!home.logo && (
								<span className="inline-block align-middle w-28 h-14 rounded-sm bg-white">
									<img src={getCDNURL(home.logo, 220, 110)} alt={home.title} className="w-full h-full object-contain rounded-sm" />
								</span>
							)}
							<div>
								<div
									className={clsx('text-base font-semibold break-words whitespace-normal line-clamp-2', {
										'text-orange-500 hover:underline': home.title !== 'TBD'
									})}
									onClick={(e) => handleRedirectToTeamDetailsPage(home, e)}
									role="presentation"
								>
									{home.title}
								</div>

								<div className="text-sm text-gray-500">Home {!!home.ranking && <span className="ml-1">#{home.ranking}</span>}</div>
							</div>
						</div>
					</div>
				</div>

				{!!matchup?.venue && (
					<div className="flex items-center gap-2 text-sm text-gray-500">
						<FontAwesomeIcon icon={faLocationDot} className="h-4 w-4 text-gray-400" />
						<span>{matchup.venue}</span>
					</div>
				)}
			</div>
		</CustomLink>
	);
};
